import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { X } from 'lucide-react';

interface RejectReasonDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  entryCount?: number;
  loading?: boolean;
}

const RejectReasonDialog: React.FC<RejectReasonDialogProps> = ({
  open,
  onClose,
  onConfirm,
  entryCount = 1,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason.trim());
      setReason('');
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const entryText = entryCount === 1 ? 'Entry' : 'Entries';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <X className="h-5 w-5 text-red-500" />
            {t('approvals.rejectDialog.title', 'Reject ' + entryCount + ' ' + entryText, { count: entryCount })}
          </DialogTitle>
          <DialogDescription>
            {t('approvals.rejectDialog.description', 'Please provide a reason for rejection. This will be visible to the user.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason">
              {t('approvals.rejectDialog.reasonLabel', 'Reason (required)')}
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('approvals.rejectDialog.placeholder', 'e.g., Hours exceed project budget, incorrect order assigned...')}
              className="min-h-[100px]"
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!reason.trim() || loading}
            variant="destructive"
          >
            {loading ? t('common.loading', 'Loading...') : t('approvals.rejectDialog.confirm', 'Reject')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RejectReasonDialog;
